import { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Box,
  Divider,
  Drawer,
  Grid,
  Hidden,
  IconButton,
  Link,
  List,
  ListItemText,
  Theme,
  Toolbar,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { makeStyles } from "@mui/styles";

import ProfileIcon from "assets/images/homepage/profile";
import { IMenuItem, MenuItems } from "common";
import ListItem from "components/shared/ListItem";
import LeftContent from "components/wrappers/LeftContent";
import useAuth from "hooks/useAuth";
import { colors } from "theme";

const useStyles = makeStyles((theme: Theme) => ({
  sidebarMenuItem: {
    "& > .MuiTypography-root": {
      fontWeight: 600,
    },
  },
  sidebarMenuListItem: { 
    '&.MuiListItem-root.Mui-selected': { backgroundColor: colors.catSkill},
    '&.MuiListItem-root.Mui-selected:hover': { backgroundColor: colors.catSkill}
  },
  hamburgerMenu: {
    marginRight: 6,
  },
  sidebarDrawer: {
    width: 328,
  },
  divider: {
    backgroundColor: colors.ocean[600],
  },
  linkP2: {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.body2.fontSize,
    fontWeight: theme.typography.body2.fontWeight,
    lineHeight: theme.typography.body2.lineHeight,
    letterSpacing: theme.typography.body2.letterSpacing,
    '&:hover': { color: theme.palette.primary.main}
  }
}));

const SidebarMenu = () => {
  const classes = useStyles();
  const history = useHistory();
  const { pathname } = useLocation();
  const { hasPermission, logout } = useAuth();

  return (
    <List component="nav" disablePadding dense>
      {MenuItems.filter(
        (menuItem: IMenuItem) =>
          !menuItem.validRoles || hasPermission(menuItem.validRoles)
      ).map((menuItem: IMenuItem, index: number) => (
        <ListItem
          className={classes.sidebarMenuListItem}
          selected={
            menuItem.link !== "/" && pathname.indexOf(menuItem.link) === 0
          }
          button
          key={index}
          onClick={() => history.push(menuItem.link)}
        >
          <ListItemText
            primary={menuItem.label}
            className={classes.sidebarMenuItem}
          />
        </ListItem>
      ))}
      <Hidden mdUp>
        <Divider className={classes.divider} />
        <ListItem button key={MenuItems.length} onClick={logout}>
          <ListItemText primary="Log Out" className={classes.sidebarMenuItem} />
        </ListItem>
      </Hidden>
    </List>
  );
};

const Sidebar = () => {
  const classes = useStyles();
  const { logout, user } = useAuth();
  const [visibleSidebar, setVisibleSidebar] = useState<boolean>(false);
  const username = `${user?.firstName} ${user?.lastName}`;

  return <>
    <Hidden mdUp>
      <LeftContent>
        <Toolbar variant="dense">
          <IconButton
            edge="start"
            color="primary"
            className={classes.hamburgerMenu}
            onClick={() => setVisibleSidebar(true)}
            size="large">
            <MenuIcon />
          </IconButton>
          <Typography variant="h4">{username}</Typography>
        </Toolbar>
        <Drawer
          open={visibleSidebar}
          PaperProps={{ className: classes.sidebarDrawer }}
          onClose={() => setVisibleSidebar(false)}
        >
          <Box paddingX={3} paddingY={2}>
            <SidebarMenu />
          </Box>
        </Drawer>
      </LeftContent>
    </Hidden>
    <Hidden mdDown>
      <LeftContent>
        <Grid container alignItems="center">
          <Box p={1}>
            <ProfileIcon />
          </Box>
          <Box ml={0.75}>
            <Typography variant="h4">{username}</Typography>
            <Link component="button" onClick={logout} color="secondary" className={classes.linkP2}>
              Log Out
            </Link>
          </Box>
        </Grid>
        <Box mt={2} mb={2}>
          <SidebarMenu />
        </Box>
      </LeftContent>
    </Hidden>
  </>;
};

export default Sidebar;
